import { FormGroup, FormLabel, Stack } from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import CalendarType from 'common/enums/calendarTypes';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServicesAction, getServices, getServicesSavePending } from 'redux/slices/iCalendarV4';
import { getAvailabilities } from 'redux/slices/myAvailabilitySlice';
import { getNestedDialogStateData } from 'redux/slices/nestedDialogSlice';
import DateOverride from '../components/DateOverride';
import OutOfTheOffice from '../components/OutOfTheOffice';
import WorkingHours from '../components/WorkingHours';
import MyAvailabilityFormServices from './MyAvailabilityFormServices';

function getUniqueServicesWeeklySchedule(objects, myAvId) {
    const allServices = objects.flatMap((obj) => (obj.id !== myAvId ? obj?.services || [] : []));

    const uniqueServices = [];
    const ids = new Set();

    allServices
        .filter((item) => item?.service)
        .forEach((item) => {
            const service = item.service;

            if (!ids.has(service.id)) {
                ids.add(service.id);
                uniqueServices.push(service);
            }
        });

    return uniqueServices;
}

const INITIAL_DAY_SCHEDULE = {
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
};

const MyAvailabilityForm = (props) => {
    const { title, availability, onSubmit, onClose, userGroupId, type, isServiceForm } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const allServices = useSelector(getServices);
    const availabilities = useSelector(getAvailabilities);
    const savePending = useSelector(getServicesSavePending);
    const nestedDialogStateData = useSelector(getNestedDialogStateData);
    const timeZone = useSettingValue(SettingsKey.TIMEZONE) || 'Europe/Madrid';
    const [selectedServices, setSelectedServices] = useState([]);
    const [services, setServices] = useState([]);

    useEffect(() => {
        if (!isServiceForm) {
            dispatch(fetchServicesAction({ queryParams: { page: 1, limit: 50, userGroupId } }));
        }
    }, [dispatch, isServiceForm, userGroupId]);

    useEffect(() => {
        if (availabilities) {
            let newServices = [];
            const availabilitiesServices = getUniqueServicesWeeklySchedule(availabilities, availability?.id);
            newServices = allServices.filter(
                (service) =>
                    !availabilitiesServices.some((aService) => aService.id === service.id) &&
                    (userGroupId ? service.userGroupId === userGroupId : true),
            );

            setServices(newServices);
        }
    }, [allServices, availabilities, availability?.id, userGroupId]);

    useEffect(() => {
        if (availability?.services) {
            const services = availability.services.map((temp) => {
                const newService = allServices?.find((service) => service.id === temp.service.id) || {};
                return {
                    ...temp.service,
                    ...newService,
                };
            });
            setSelectedServices(services);
        }
    }, [allServices, availability?.services]);

    const initialForm = useMemo(() => {
        return {
            id: availability?.id || '',
            name: availability?.name || '',
            weeklySchedule: { ...INITIAL_DAY_SCHEDULE, ...availability?.weeklySchedule },
            overrides: availability?.overrides || {},
            unavailableDates: availability?.unavailableDates || [],
            active: availability?.active ?? true,
        };
    }, [availability]);

    const [fields, handleFieldChange, resetForm] = useFormFields(nestedDialogStateData || initialForm);

    function cancel(e) {
        e.preventDefault();
        resetForm(initialForm);
        onClose('cancel');
    }

    function handleSubmit(e) {
        e.preventDefault();
        onSubmit(
            {
                ...fields,
                timezone: timeZone,
                serviceIds: selectedServices.map((service) => service.id),
            },
            () => {
                resetForm(initialForm);
                onClose('success');
            },
            (error) => {
                if (error?.response?.data?.description) {
                    enqueueSnackbar(error.response?.data?.description, {
                        variant: 'error',
                    });
                } else {
                    enqueueSnackbar(t('app.common.error'), {
                        variant: 'error',
                    });
                }
            },
        );
    }

    return (
        <LayoutForm
            title={title}
            customClasses={cx(!isServiceForm && flexBox.vH70)}
            noPadding={isServiceForm}
        // confirmDlg={{
        //     open: openCancelDlg,
        //     accept: finalCancel,
        //     cancel: cancelDlg,
        //     titleDlg: confirmTitle,
        //     messageDlg: confirmMessage,
        // }}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                    <ValidatorForm
                        onSubmit={() => { }}
                        onError={(e) => { }}
                        className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
                    >
                        <Stack
                            direction="column"
                            className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
                        >
                            <Stack direction="column" spacing={3}>
                                {type === CalendarType.AGENT ? (
                                    <>
                                        <FormGroup>
                                            <FormLabel>{t('profile.myAvailability.form.title')}</FormLabel>
                                            <CustomTextValidator
                                                name="name"
                                                fullWidth={true}
                                                variant="outlined"
                                                size="small"
                                                autoComplete="new-name"
                                                value={fields.name}
                                                onChange={handleFieldChange}
                                                validators={['maxStringLength: 100']}
                                                errorMessages={[t('profile.myAvailability.form.titleMaxLength')]}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <WorkingHours fields={fields} handleFieldChange={handleFieldChange} />
                                        </FormGroup>

                                        {isServiceForm ? null : (
                                            <MyAvailabilityFormServices
                                                services={services}
                                                selectedServices={selectedServices}
                                                setSelectedServices={setSelectedServices}
                                            />
                                        )}
                                    </>
                                ) : null}
                                <FormGroup>
                                    <OutOfTheOffice
                                        fields={fields}
                                        handleFieldChange={handleFieldChange}
                                        timeZone={timeZone}
                                        type={type}
                                    />
                                </FormGroup>
                                {type === CalendarType.AGENT ? (
                                    <>
                                        <FormGroup>
                                            <DateOverride fields={fields} handleFieldChange={handleFieldChange} />
                                        </FormGroup>
                                    </>
                                ) : null}
                            </Stack>
                        </Stack>
                    </ValidatorForm>
                </Stack>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                    className={flexBox.paddingTop2}
                >
                    <ActionButton onClick={cancel} color="default" fitWidth>
                        {t('app.common.cancel')}
                    </ActionButton>

                    <ActionButton color="primary" autoFocus fitWidth disabled={savePending} onClick={handleSubmit}>
                        {t('app.common.save')}
                    </ActionButton>
                </Stack>
            </Stack>
        </LayoutForm>
    );
};

export default MyAvailabilityForm;
